import React, { useState, useMemo } from 'react';
import { Grid, Typography, ToggleButton, ToggleButtonGroup, MenuItem, Select, FormControl, TextField } from '@mui/material';
import { LocalizationProvider, DesktopDatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  startOfMonth, endOfMonth, startOfQuarter, endOfQuarter,
  startOfYear, endOfYear, setMonth, setYear, getYear
} from 'date-fns';

// @assets
import { DateFilterStyle } from '../../../../assets/css/payroll-style'

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const QUARTERS = ['Q1 (Jan–Mar)', 'Q2 (Apr–Jun)', 'Q3 (Jul–Sep)', 'Q4 (Oct–Dec)'];
const currentYear = getYear(new Date());
const YEARS = Array.from({ length: 6 }, (_, i) => currentYear - 5 + i + 1).reverse();

export default function DateRangeFilter({ startDate, endDate, onChange, errors = {}, touched = {} }) {
  const style = useMemo(() => DateFilterStyle(), []);
  const [mode, setMode] = useState('custom');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedQuarter, setSelectedQuarter] = useState(0);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const applyRange = (start, end) => onChange({ startDate: start, endDate: end });

  const handleModeChange = (_, newMode) => {
    if (!newMode) return;
    setMode(newMode);
    if (newMode === 'month') {
      const base = setYear(setMonth(new Date(), selectedMonth), selectedYear);
      applyRange(startOfMonth(base), endOfMonth(base));
    } else if (newMode === 'quarter') {
      const base = setYear(setMonth(new Date(), selectedQuarter * 3), selectedYear);
      applyRange(startOfQuarter(base), endOfQuarter(base));
    } else if (newMode === 'yearly') {
      const base = setYear(new Date(), selectedYear);
      applyRange(startOfYear(base), endOfYear(base));
    }
  };

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    const base = setYear(setMonth(new Date(), month), selectedYear);
    applyRange(startOfMonth(base), endOfMonth(base));
  };

  const handleQuarterChange = (quarter) => {
    setSelectedQuarter(quarter);
    const base = setYear(setMonth(new Date(), quarter * 3), selectedYear);
    applyRange(startOfQuarter(base), endOfQuarter(base));
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    if (mode === 'month') {
      const base = setYear(setMonth(new Date(), selectedMonth), year);
      applyRange(startOfMonth(base), endOfMonth(base));
    } else if (mode === 'quarter') {
      const base = setYear(setMonth(new Date(), selectedQuarter * 3), year);
      applyRange(startOfQuarter(base), endOfQuarter(base));
    } else if (mode === 'yearly') {
      applyRange(startOfYear(setYear(new Date(), year)), endOfYear(setYear(new Date(), year)));
    }
  };

  const handleDateChange = (date, field) => {
    onChange({ startDate, endDate, [field]: date });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Grid container spacing={1}>

        <Grid item xs={12} sx={{ marginBottom: '2px' }}>
          <Typography sx={style.label}>Start Date</Typography>
        </Grid>

        <Grid item xs={12}>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={handleModeChange}
            sx={style.toggleGroup}
          >
            {['custom', 'month', 'quarter', 'yearly'].map((m) => (
              <ToggleButton key={m} value={m} sx={style.toggleBtn}>
                {m.charAt(0).toUpperCase() + m.slice(1)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Grid>

        {mode === 'month' && (
          <>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <Select value={selectedMonth} sx={style.fieldsInput} onChange={(e) => handleMonthChange(e.target.value)}>
                  {MONTHS.map((m, i) => <MenuItem key={i} value={i} sx={style.menuItem}>{m}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <Select value={selectedYear} sx={style.fieldsInput} onChange={(e) => handleYearChange(e.target.value)}>
                  {YEARS.map(y => <MenuItem key={y} value={y} sx={style.menuItem}>{y}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </>
        )}

        {mode === 'quarter' && (
          <>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <Select value={selectedQuarter} sx={style.fieldsInput} onChange={(e) => handleQuarterChange(e.target.value)}>
                  {QUARTERS.map((q, i) => <MenuItem key={i} value={i} sx={style.menuItem}>{q}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <Select value={selectedYear} sx={style.fieldsInput} onChange={(e) => handleYearChange(e.target.value)}>
                  {YEARS.map(y => <MenuItem key={y} value={y} sx={style.menuItem}>{y}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </>
        )}

        {mode === 'yearly' && (
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <Select value={selectedYear} onChange={(e) => handleYearChange(e.target.value)}>
                {YEARS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        )}

        {mode === 'custom' && (
          <>
            <Grid item xs={5.5}>
              <DesktopDatePicker
                value={startDate}
                onChange={(date) => handleDateChange(date, 'startDate')}
                allowSameDateSelection
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id="startDate"
                    name="startDate"
                    size="small"
                    error={touched.startDate && Boolean(errors.startDate)}
                    helperText={touched.startDate && errors.startDate}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={1}>
              <Typography sx={style.to}>to</Typography>
            </Grid>
            <Grid item xs={5.5}>
              <DesktopDatePicker
                value={endDate}
                onChange={(date) => handleDateChange(date, 'endDate')}
                allowSameDateSelection
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id="endDate"
                    name="endDate"
                    size="small"
                    error={touched.endDate && Boolean(errors.endDate)}
                    helperText={touched.endDate && errors.endDate}
                    fullWidth
                  />
                )}
              />
            </Grid>
          </>
        )}

      </Grid>
    </LocalizationProvider>
  );
}